/**
 * Units to add for an addon checkbox. Reads `data-quantity` (set from the
 * addon block's Quantity setting) and falls back to 1 for older markup.
 * @param {HTMLInputElement} checkbox
 */
function addonQuantity(checkbox) {
  const parsed = Number.parseInt(checkbox.dataset.quantity || '1', 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

// Bundle parents reject parent_id. Nest keys match hybrid-cart.
function nestBundleItems(items) {
  if (!items.some((item) => item.parent_id != null)) return items
  const nestId = crypto.randomUUID()
  return items.map((item) => {
    if (item.parent_id == null) {
      return { ...item, properties: { ...(item.properties || {}), _HC_NEST_ID: nestId } }
    }
    const { parent_id, ...rest } = item
    return { ...rest, properties: { ...(rest.properties || {}), _HC_PARENT_KEY: nestId } }
  })
}

class BodyProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return
    this.initialized = true
    this.isInitialLoad = true
    this.variants = this.readVariants()
    this.automaticDiscountByVariantId = this.readAutomaticDiscountByVariantId()
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })
    this.setupEvents()
    this.update({ syncMedia: true })
  }

  readVariants() {
    const variantsData = this.querySelector('#product-variants-data')
    if (!variantsData) return []
    try {
      const variants = JSON.parse(variantsData.textContent || '[]')
      return Array.isArray(variants) ? variants : []
    } catch (error) {
      console.error('[body-product-form] Error parsing product variants:', error)
      return []
    }
  }

  readAutomaticDiscountByVariantId() {
    const discountsData = this.querySelector('#variant-automatic-discounts')
    if (!discountsData) return {}
    try {
      const discounts = JSON.parse(discountsData.textContent || '{}')
      return discounts && typeof discounts === 'object' ? discounts : {}
    } catch (error) {
      console.error('[body-product-form] Error parsing variant automatic discounts:', error)
      return {}
    }
  }

  getAutomaticDiscountCents(variant) {
    if (variant) {
      const fromMap = Number(this.automaticDiscountByVariantId[String(variant.id)])
      if (Number.isFinite(fromMap) && fromMap >= 0) return fromMap
    }
    return Number(this.getAttribute('automatic-discount')) || 0
  }

  setupEvents() {
    this.querySelectorAll('[data-option-input]').forEach((input) => {
      input.addEventListener('change', () => this.update({ syncMedia: true, syncUrl: true }))
    })

    this.setupAddToCart()
  }

  setupAddToCart() {
    // Use document-level delegation so sticky nav buttons (rendered outside this
    // custom element in #pdp-sticky-nav, #pdp-sticky-nav-overlay, and #pdp-mobile-cta)
    // are also handled.
    this.onDocumentAddToCartClick =
      this.onDocumentAddToCartClick ||
      ((e) => {
        if (!(e.target instanceof Element)) return
        const button = e.target.closest('[data-add-to-cart]')
        if (!button) return
        const variant = this.getSelectedVariant()
        const allowUnavailable = this.hasAttribute('data-allow-unavailable-options')
        if (!variant || (!allowUnavailable && !variant.available)) return
        button.dataset.loading = ''
        this.addToCart(variant, button)
      })
    document.addEventListener('click', this.onDocumentAddToCartClick)
  }

  disconnectedCallback() {
    if (this.onDocumentAddToCartClick) {
      document.removeEventListener('click', this.onDocumentAddToCartClick)
    }
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-input]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value)
  }

  getSelectedVariant() {
    const selectedOptions = this.getSelectedOptions()
    if (!selectedOptions.length) return this.variants[0] || null
    return (
      this.variants.find((variant) => this.variantMatchesOptions(variant, selectedOptions)) ||
      this.variants.find((variant) => variant.available) ||
      this.variants[0] ||
      null
    )
  }

  variantMatchesOptions(variant, options) {
    const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)
    return options.every((option, index) => variantOptions[index] === option)
  }

  findVariantForOption(position, value) {
    const selectedOptions = this.getSelectedOptions()
    selectedOptions[position - 1] = value
    return this.variants.find((variant) => this.variantMatchesOptions(variant, selectedOptions))
  }

  update({ syncMedia: doSyncMedia = false, syncUrl = false } = {}) {
    this.updateOptionAvailability()
    const variant = this.getSelectedVariant()
    if (!variant) return

    this.setAttribute('variant-id', String(variant.id))
    const idInput = this.querySelector('input[name="id"]')
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id)

    this.updatePrice(variant)
    this.updateSelectedLabels()
    this.updateAddToCart(variant)
    if (syncUrl) {
      this.syncUrl(variant)
    }
    if (doSyncMedia) {
      const placement = this.isInitialLoad ? 'after-video' : 'first'
      this.isInitialLoad = false
      this.syncMedia(variant, { placement })
    }
  }

  updateOptionAvailability() {
    // Opt-out: keep every option selectable regardless of availability
    // (used by the spray form so all quantity cards stay clickable/visible).
    if (this.hasAttribute('data-allow-unavailable-options')) {
      this.querySelectorAll('[data-option-input]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.disabled = false
      })
      return
    }

    this.querySelectorAll('[data-option-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return
      const position = Number(input.dataset.optionPosition)
      const candidate = this.findVariantForOption(position, input.value)
      input.disabled = !candidate || candidate.available === false
    })

    const checked = this.querySelector('[data-option-input]:checked:disabled')
    if (checked instanceof HTMLInputElement) {
      const replacement = this.querySelector(`[name="${CSS.escape(checked.name)}"]:not(:disabled)`)
      if (replacement instanceof HTMLInputElement) replacement.checked = true
    }
  }

  updatePrice(variant) {
    const price = Number(variant.price) || 0
    const automaticDiscount = this.getAutomaticDiscountCents(variant)
    const discountedPrice = Math.max(price - automaticDiscount, 0)
    const variantCompareAt = Number(variant.compare_at_price) || 0
    const compareAt = variantCompareAt > price ? variantCompareAt : price > discountedPrice ? price : 0
    const hasCompareAt = compareAt > discountedPrice

    document.querySelectorAll('[data-main-product-price]').forEach((el) => {
      el.textContent = this.formatter.format(discountedPrice / 100)
    })

    document.querySelectorAll('[data-main-product-compare-at-price]').forEach((el) => {
      el.textContent = `(Total Value ${this.formatter.format(compareAt / 100)})`
    })

    document.querySelectorAll('[data-apex-compare-line]').forEach((row) => {
      if (row instanceof HTMLElement) row.classList.toggle('hidden', !hasCompareAt)
    })

    document.querySelectorAll('[data-apex-save-pill]').forEach((el) => {
      if (el instanceof HTMLElement) el.classList.toggle('hidden', !hasCompareAt)
    })

    document.querySelectorAll('[data-promo-text]').forEach((el) => {
      if (el.dataset.autoDiscountPromo === 'true') return
      const originalText = el.dataset.promoOriginalText || el.textContent || ''
      const formattedSavings = this.formatter.format(Math.max(0, compareAt - discountedPrice) / 100)
      el.textContent = originalText.replace(/\$[\d,]+(?:\.\d{2})?/, formattedSavings)
    })

    const sprayBasePrice = Number(this.dataset.sprayBasePrice)
    if (Number.isFinite(sprayBasePrice) && sprayBasePrice > 0) {
      const option1 = String(variant.option1 || variant.options?.[0] || '')
      const count = Math.max(1, parseInt(option1.split(' ')[0], 10) || 1)
      const fullPrice = sprayBasePrice * count
      const showStrikethrough = fullPrice > discountedPrice

      this.querySelectorAll('[data-spray-strikethrough-price]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return
        el.classList.toggle('hidden', !showStrikethrough)
        if (showStrikethrough) {
          el.textContent = this.formatter.format(fullPrice / 100)
        }
      })
    }
  }

  updateSelectedLabels() {
    this.querySelectorAll('[data-selected-option-label][data-option-position]').forEach((label) => {
      const position = label.getAttribute('data-option-position')
      const input = this.querySelector(`[data-option-input][data-option-position="${position}"]:checked`)
      if (!(input instanceof HTMLInputElement)) return
      label.textContent = input.value
    })
  }

  updateAddToCart(variant) {
    const forceOOS = this.hasAttribute('data-force-out-of-stock')
    const allowUnavailable = this.hasAttribute('data-allow-unavailable-options')
    this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      const text = button.querySelector('[data-text]')
      const unavailable = forceOOS || (!allowUnavailable && variant.available === false)
      button.toggleAttribute('disabled', unavailable)
      button.classList.toggle('pointer-events-none', unavailable)
      button.classList.toggle('cursor-not-allowed', unavailable)
      if (text) text.textContent = unavailable ? 'SOLD OUT' : 'Add to Cart'
    })
  }

  getAutoAddons() {
    try {
      const autoAddons = JSON.parse(this.getAttribute('auto-addons') || '[]')
      return Array.isArray(autoAddons) ? autoAddons : []
    } catch (error) {
      console.error('[body-product-form] Error parsing auto-addons:', error)
      return []
    }
  }

  getSelectedAddons() {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
      .filter((input) => input instanceof HTMLInputElement && input.value !== '')
      .map((input) => ({ quantity: addonQuantity(input), id: Number(input.value), properties: { _addon: true } }))
  }

  addToCart(variant, button) {
    const parentVariantId = Number(variant.id)
    const items = [
      ...this.getAutoAddons().map((addon) => ({ ...addon, parent_id: parentVariantId })),
      { quantity: 1, id: parentVariantId },
      ...this.getSelectedAddons().map((addon) => ({ ...addon, parent_id: parentVariantId })),
    ]

    // Shopify applies lines in API order; reverse so the main product appears first in cart display.
    let cartItems = window.igApplyCartNesting(items.reverse())
    if (this.dataset.bundle === 'true') cartItems = nestBundleItems(cartItems)
    const payload = { items: cartItems }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json()
          return alert(error.description || error.message)
        }
        window.HybridCart.open()
      })
      .catch((error) => {
        console.error('[body-product-form] Error adding to cart:', error)
      })
      .finally(() => {
        delete button.dataset.loading
      })
  }

  syncUrl(variant) {
    if (typeof window === 'undefined' || !window.history?.replaceState) return
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get('variant') === String(variant.id)) return
      url.searchParams.set('variant', String(variant.id))
      window.history.replaceState({}, '', url.toString())
    } catch (_) {}
  }

  getVariantMediaIds(variant) {
    return [variant.featured_media?.id, variant.featured_image?.id].filter(Boolean).map(String)
  }

  findSlideByMediaIds(slides, mediaIds) {
    const desired = new Set(mediaIds)
    return slides.find(
      (slide) =>
        desired.has(slide.getAttribute?.('data-media-id')) || desired.has(slide.getAttribute?.('data-image-id'))
    )
  }

  rememberOriginalMediaOrder(mediaRoot) {
    if (mediaRoot._defaultProductOriginalMainSlides) return
    const mainList = mediaRoot.querySelector('[data-product-media-v2-main] .splide__list')
    const thumbsList = mediaRoot.querySelector('[data-product-media-v2-thumbs-list]')
    mediaRoot._defaultProductOriginalMainSlides = mainList ? Array.from(mainList.children) : []
    mediaRoot._defaultProductOriginalThumbSlides = thumbsList ? Array.from(thumbsList.children) : []
  }

  reorderMediaList(list, originalSlides, selectedSlide, placement) {
    if (!list) return []
    const seeItSlide =
      placement === 'after-video'
        ? originalSlides.find((slide) => slide.hasAttribute('data-see-it-in-action-main-slide'))
        : null
    const leadingSlides = [seeItSlide, selectedSlide].filter(Boolean)
    if (!leadingSlides.length) return []

    const orderedSlides = [...leadingSlides, ...originalSlides.filter((slide) => !leadingSlides.includes(slide))]
    list.replaceChildren(...orderedSlides)
    return orderedSlides
  }

  reorderThumbList(mediaRoot, mainOrderedSlides) {
    const thumbsList = mediaRoot.querySelector('[data-product-media-v2-thumbs-list]')
    const originalThumbSlides = mediaRoot._defaultProductOriginalThumbSlides || []
    if (!thumbsList || !originalThumbSlides.length || !mainOrderedSlides.length) return

    const orderedThumbs = mainOrderedSlides
      .map((slide) => slide.getAttribute('data-body-slide-index'))
      .map((slideIndex) =>
        originalThumbSlides.find((thumb) => thumb.getAttribute('data-body-slide-index') === slideIndex)
      )
      .filter(Boolean)

    orderedThumbs.forEach((thumb, index) => {
      const button = thumb.querySelector('[data-product-media-v2-thumb]')
      if (button) button.dataset.slideIndex = String(index)
    })
    if (orderedThumbs.length === mainOrderedSlides.length) {
      thumbsList.replaceChildren(...orderedThumbs)
    }
  }

  updateFloatingImage(selectedSlide) {
    const floatImg = document.querySelector(
      `[data-floating-product-image][data-floating-product-image-section="${CSS.escape(this.dataset.sectionId)}"] img`
    )
    const selectedImg = selectedSlide?.querySelector('img')
    const src = selectedImg?.currentSrc || selectedImg?.src
    if (!floatImg || !src) return
    const url = new URL(src, window.location.href)
    url.searchParams.set('width', '300')
    url.searchParams.set('height', '300')
    url.searchParams.set('crop', 'center')
    floatImg.removeAttribute('srcset')
    floatImg.width = 300
    floatImg.height = 300
    floatImg.src = url.toString()
  }

  syncMedia(variant, { placement = 'first', attempt = 0 } = {}) {
    const sectionId = this.dataset.sectionId
    if (!sectionId) return

    const mediaRoot = document.querySelector(`[data-product-media-v2="${CSS.escape(sectionId)}"]`)
    const splide = mediaRoot?._bodyProductMediaSplide
    if (!mediaRoot || !splide) {
      if (attempt < 60) {
        requestAnimationFrame(() => this.syncMedia(variant, { placement, attempt: attempt + 1 }))
      }
      return
    }

    this.rememberOriginalMediaOrder(mediaRoot)
    const mainList = mediaRoot.querySelector('[data-product-media-v2-main] .splide__list')
    const originalMainSlides = mediaRoot._defaultProductOriginalMainSlides || []

    const mediaIds = this.getVariantMediaIds(variant)
    const selectedSlide = mediaIds.length ? this.findSlideByMediaIds(originalMainSlides, mediaIds) : null

    const orderedSlides = this.reorderMediaList(mainList, originalMainSlides, selectedSlide, placement)
    if (orderedSlides.length) {
      this.reorderThumbList(mediaRoot, orderedSlides)
      if (selectedSlide) {
        this.updateFloatingImage(selectedSlide)
      }

      try {
        splide.refresh()
        mediaRoot._bodyProductThumbSplide?.refresh?.()
        mediaRoot._applyProductGalleryRoles?.()
      } catch (_) {}

      try {
        splide.go(0)
      } catch (_) {}
    }
  }
}

if (!customElements.get('body-product-form')) {
  customElements.define('body-product-form', BodyProductForm)
}
